import { newConversationId } from '../../../../lib/askStream';
import type {
  CountReceipt,
  NotebookChapterSpec,
  SuggestedAction,
} from '../../types';
import { summarizePrompt } from '../assistant/chapterGate';
import type {
  ThreadAssistantMessage,
  ThreadMessage,
  ThreadMeta,
} from './threadTypes';

/** The notebook database before threads (docs/18 §6); read once, never written. */
export const LEGACY_NOTEBOOK_DB = 'aleno-maitre-notebook';
const LEGACY_STORE = 'notebooks';

export type LegacyNotebookDoc = {
  scopeKey: string;
  conversationId?: string;
  chapters?: Array<{
    id: string;
    index: number;
    question: string;
    answer: string;
    spec: NotebookChapterSpec;
    createdAt: string;
  }>;
  side?: Array<{
    id: string;
    role: 'user' | 'assistant';
    text: string;
    error?: boolean;
    suggestedActions?: SuggestedAction[];
    receipts?: CountReceipt[];
  }>;
  updatedAt?: string;
};

const SERVER_ID = /^[A-Za-z0-9_-]{8,64}$/;

/** The old canvas story and the rail reply become one answer text. */
function joinAnswer(story: string, rail: string): string {
  const a = story.trim();
  const b = rail.trim();
  if (!a) return b;
  if (!b || a.includes(b)) return a;
  return `${a}\n\n${b}`;
}

/** One notebook (per scope) becomes one thread. Rail lines become messages;
 *  each chapter becomes the card of the answer to its question, keeping the
 *  chapter id so Board tiles still open it. */
export function notebookDocToThread(
  doc: LegacyNotebookDoc,
): { meta: ThreadMeta; messages: ThreadMessage[] } | null {
  const chapters = doc.chapters ?? [];
  const side = doc.side ?? [];
  if (chapters.length === 0 && side.length === 0) return null;
  const fallbackTime = doc.updatedAt || new Date(0).toISOString();
  const used = new Set<string>();
  const messages: ThreadMessage[] = [];
  let question = '';
  for (const line of side) {
    if (line.role === 'user') {
      question = line.text;
      messages.push({
        id: line.id,
        role: 'user',
        text: line.text,
        createdAt: fallbackTime,
        scopeKey: doc.scopeKey,
      });
      continue;
    }
    const message: ThreadAssistantMessage = {
      id: line.id,
      role: 'assistant',
      text: line.text,
      createdAt: fallbackTime,
      ...(question ? { question } : {}),
      ...(line.receipts?.length ? { receipts: line.receipts } : {}),
      ...(line.suggestedActions?.length ? { suggestedActions: line.suggestedActions } : {}),
      ...(line.error ? { error: true } : {}),
    };
    const chapter =
      !line.error && question
        ? chapters.find(c => !used.has(c.id) && c.question === question)
        : undefined;
    if (chapter) {
      used.add(chapter.id);
      message.id = chapter.id;
      message.card = chapter.spec;
      message.createdAt = chapter.createdAt || fallbackTime;
      message.text = joinAnswer(chapter.answer || chapter.spec.story || '', line.text);
    }
    messages.push(message);
    question = '';
  }
  for (const chapter of chapters) {
    if (used.has(chapter.id)) continue;
    const at = chapter.createdAt || fallbackTime;
    messages.push(
      { id: `${chapter.id}_q`, role: 'user', text: chapter.question, createdAt: at, scopeKey: doc.scopeKey },
      {
        id: chapter.id,
        role: 'assistant',
        text: chapter.answer || chapter.spec.story || '',
        createdAt: at,
        question: chapter.question,
        card: chapter.spec,
      },
    );
  }
  const firstQuestion = messages.find(m => m.role === 'user')?.text ?? '';
  const times = chapters.map(c => c.createdAt).filter(Boolean).sort();
  const meta: ThreadMeta = {
    id: doc.conversationId && SERVER_ID.test(doc.conversationId) ? doc.conversationId : newConversationId(),
    title: summarizePrompt(firstQuestion || 'Notes'),
    createdAt: times[0] ?? fallbackTime,
    updatedAt: doc.updatedAt || times[times.length - 1] || fallbackTime,
    messageCount: messages.length,
    scopeKey: doc.scopeKey,
    cardIds: messages.filter(m => m.role === 'assistant' && m.card).map(m => m.id),
  };
  return { meta, messages };
}

/** Every notebook in the legacy database; [] when it never existed. */
export function readLegacyNotebooks(): Promise<LegacyNotebookDoc[]> {
  return new Promise(resolve => {
    if (typeof indexedDB === 'undefined') {
      resolve([]);
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(LEGACY_NOTEBOOK_DB);
    } catch {
      resolve([]);
      return;
    }
    // A missing database would be created by open(); abort so it stays absent.
    req.onupgradeneeded = () => req.transaction?.abort();
    req.onerror = () => resolve([]);
    req.onblocked = () => resolve([]);
    req.onsuccess = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(LEGACY_STORE)) {
        db.close();
        resolve([]);
        return;
      }
      const all = db.transaction(LEGACY_STORE, 'readonly').objectStore(LEGACY_STORE).getAll();
      all.onsuccess = () => {
        db.close();
        resolve((all.result as LegacyNotebookDoc[] | undefined) ?? []);
      };
      all.onerror = () => {
        db.close();
        resolve([]);
      };
    };
  });
}
